
import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practice: '',
    specialty: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d62hss8v3sjav1bavjsg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          practice: '',
          specialty: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const specialties = [
    'Hormone Therapy / BHRT',
    'Longevity / Anti-Aging Medicine',
    'Functional / Integrative Medicine',
    'Women\'s Health / Menopause',
    'Men\'s Health / Wellness',
    'Preventive / Internal Medicine',
    'Cardiology',
    'Endocrinology',
    'Medical Spa',
    'Primary Care',
    'Urology',
    'Wellness Center',
    'Other'
  ];

  return (
    <section id="contact" className="py-24 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              GET STARTED
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Book Your Free Strategy Call
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-8">
              Ready to grow your practice with HIPAA-compliant marketing? Schedule a free 30-minute strategy call to discuss your goals and see how we can help.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-[#7C3AED] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">30-Minute Strategy Session</h4>
                  <p className="text-sm text-gray-600">Discuss your practice goals and marketing challenges.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-[#7C3AED] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Custom Growth Plan</h4>
                  <p className="text-sm text-gray-600">Receive a tailored marketing roadmap for your specialty.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-[#7C3AED] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">No Obligation</h4>
                  <p className="text-sm text-gray-600">Free consultation with no pressure or commitment.</p>
                </div>
              </div>
            </div>

            <div className="relative w-full h-64 rounded-xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/7580739bad4641ea8aaa6aabaa56944c.jpg"
                alt="Strategy consultation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="bg-[#F9FAFB] rounded-2xl p-8 lg:p-10">
            <form 
              id="strategy-call-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 outline-none transition-all text-sm"
                  placeholder="Dr. John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 outline-none transition-all text-sm"
                  placeholder="doctor@practice.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 outline-none transition-all text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="practice" className="block text-sm font-medium text-gray-700 mb-2">
                  Practice Name *
                </label>
                <input
                  type="text"
                  id="practice"
                  name="practice"
                  required
                  value={formData.practice}
                  onChange={(e) => setFormData({ ...formData, practice: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 outline-none transition-all text-sm"
                  placeholder="Your Practice Name"
                />
              </div>

              <div>
                <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-2">
                  Specialty *
                </label>
                <select
                  id="specialty"
                  name="specialty"
                  required
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select your specialty</option>
                  {specialties.map((specialty, index) => (
                    <option key={index} value={specialty}>{specialty}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your goals
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 outline-none transition-all text-sm resize-none"
                  placeholder="What are your biggest marketing challenges? What growth goals do you have?"
                />
                <div className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:from-[#6D28D9] hover:to-[#9333EA] transition-all cursor-pointer whitespace-nowrap font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Book My Free Strategy Call</span>
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <div className="font-medium text-green-800 text-sm">Request Submitted!</div>
                    <div className="text-xs text-green-600">We'll contact you within 24 hours.</div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <i className="ri-error-warning-line text-red-600"></i>
                  </div>
                  <div>
                    <div className="font-medium text-red-800 text-sm">Submission Failed</div>
                    <div className="text-xs text-red-600">Please try again or contact us directly.</div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                <i className="ri-shield-check-line text-[#7C3AED]"></i> Your information is secure and will never be shared.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
